import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import { handleAuthApiFailure } from "../utils/authSessionFailure.js";
function resolveDynamicHost(rawUrl) {
  if (!rawUrl) return rawUrl;
  const s = String(rawUrl).trim().replace(/\/$/, "");
  if (typeof window === "undefined" || !window.location || !window.location.hostname) return s;
  try {
    const urlObj = new URL(s);
    if (urlObj.hostname === "localhost" || urlObj.hostname === "127.0.0.1" || urlObj.hostname === "0.0.0.0") {
      urlObj.hostname = window.location.hostname;
      return urlObj.toString().replace(/\/$/, "");
    }
  } catch (e) {
    // ignore
  }
  return s;
}

export function getAlertApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  return resolveDynamicHost(raw || "");
}

/**
 * Alert service API base — empty string → relative `/alerts/...` (nginx proxy).
 */
export const API_BASE_URL = getAlertApiBase();


/**
 * Get authentication token
 */
export const getAuthToken = () => {
  const token = tokenManager.getToken();
  if (process.env.NODE_ENV === "development") {
    console.log("Getting auth token:", { hasToken: !!token });
  }
  return token;
};

/**
 * Generic API request function
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @returns {Promise<Object>} API response
 */
export const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const { headers: optionHeaders = {}, ...restOptions } = options;

  /**
   * Merge `options` without letting `options.headers` replace the whole header
   * object (which would drop Authorization and break POST bodies in DevTools).
   */
  const config = {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...userTimezoneHeaders(),
      ...optionHeaders,
    },
  };

  const fullUrl = `${API_BASE_URL}${endpoint}`;
  if (process.env.NODE_ENV === "development") {
    console.log("Making API request:", {
      url: fullUrl,
      endpoint,
      hasAuthHeader: !!config.headers.Authorization,
    });
  }

  try {
    const response = await fetch(fullUrl, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (process.env.NODE_ENV === "development") {
        console.error("API request failed:", {
          status: response.status,
          error: errorData,
        });
      }
      const code =
        (errorData?.error?.code && String(errorData.error.code)) ||
        (errorData?.code && String(errorData.code)) ||
        "REQUEST_FAILED";
      const message =
        (errorData?.error?.message && String(errorData.error.message)) ||
        (errorData?.message && String(errorData.message)) ||
        (Array.isArray(errorData?.errors) &&
          errorData.errors[0]?.message &&
          String(errorData.errors[0].message)) ||
        `HTTP error! status: ${response.status}`;
      if (handleAuthApiFailure(response.status, code, message, errorData)) {
        throw new Error(message);
      }
      // Create a custom error object that preserves the full API response
      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
      error.status = response.status;
      error.data = errorData;
      error.errors = errorData.errors || [];
      throw error;
    }

    return await response.json();
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("API request failed:", error);
    }
    throw error;
  }
};

/**
 * HTTP method wrapper functions
 */
export const get = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`${endpoint}?${queryString}`, { method: "GET" });
};
export const getWithoutQueryString = async (endpoint) => {
  return apiRequest(endpoint, { method: "GET" });
};
export const post = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const put = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const del = async (endpoint) => {
  return apiRequest(endpoint, { method: "DELETE" });
};

/**
 * Backend validates `query.user_id` on notification-credentials, user-settings
 * cooldown-range, and alert-rules settings routes.
 * @returns {string|null}
 */
export function getAuthUserIdForQuery() {
  const user = tokenManager.getUser();
  if (!user || typeof user !== "object") return null;
  const raw = user.id ?? user.user_id ?? user.userId;
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "" || s === "null") return null;
  return s;
}

/** @param {URLSearchParams} queryParams */
export function appendUserIdQueryParam(queryParams) {
  const uid = getAuthUserIdForQuery();
  if (uid) queryParams.append("user_id", uid);
}
